import React from 'react';

const BacktestResults = ({ 
  performanceData, 
  title = "Recent Backtest Results",
  showTitle = true,
  compact = false 
}) => {
  if (!performanceData || !performanceData.mostRecentBacktest) {
    return (
      <div style={{
        background: '#21262d',
        border: '1px solid #30363d',
        borderRadius: '8px',
        padding: '1.5rem',
        textAlign: 'center',
        color: '#8b949e'
      }}>
        <span>⚠️ No recent backtest available</span>
      </div>
    );
  }

  const backtest = performanceData.mostRecentBacktest;
  
  // Determine if the performance is positive or negative for styling
  const isPositiveReturn = backtest.cagrAnnualReturn && parseFloat(backtest.cagrAnnualReturn.replace('%', '')) > 0;
  const isPositiveTotalReturn = backtest.totalReturn && parseFloat(backtest.totalReturn.replace('%', '')) > 0;

  const cardStyle = {
    background: '#21262d',
    border: '1px solid #30363d',
    borderRadius: '8px',
    padding: compact ? '1rem' : '1.5rem',
    color: '#e6edf3'
  };

  const periodStyle = {
    background: '#262c36',
    padding: '1rem',
    borderRadius: '6px',
    borderLeft: '3px solid #58a6ff',
    marginBottom: '1rem'
  };

  const periodLabelStyle = {
    fontSize: '0.8rem',
    color: '#8b949e',
    marginBottom: '0.5rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '500'
  };

  const periodDateStyle = {
    fontWeight: '700',
    color: '#e6edf3',
    fontSize: '1.1rem'
  };

  const metricsGridStyle = {
    display: 'grid',
    gridTemplateColumns: compact ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem'
  };

  const metricCardStyle = {
    background: '#1c2128',
    border: '1px solid #30363d',
    borderRadius: '6px',
    padding: '1rem',
    textAlign: 'center'
  };

  const metricLabelStyle = {
    fontSize: '0.75rem',
    color: '#8b949e',
    marginBottom: '0.5rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const metricValueStyle = {
    fontSize: '1.4rem',
    fontWeight: '700',
    marginBottom: '0.25rem',
    lineHeight: '1.2'
  };

  const metricDescStyle = {
    fontSize: '0.7rem',
    color: '#8b949e',
    fontWeight: '400'
  };

  const infoStyle = {
    background: 'rgba(187, 128, 9, 0.15)',
    border: '1px solid rgba(187, 128, 9, 0.25)',
    borderRadius: '6px',
    padding: '0.75rem',
    textAlign: 'center',
    marginTop: '1rem'
  };

  // Helper to scale percent values by 100 and add comma separators if needed
  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';

    // Backend sends decimal values (0.15 for 15%), convert to percentage for display
    let num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return 'N/A';

    // Comma-separate thousands and limit to 2 decimal places (trailing zeros trimmed)
    const formattedNumber = num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });

    return `${formattedNumber}%`;
  };

  return (
    <div style={cardStyle}>
      {showTitle && (
        <h4 style={{ color: '#58a6ff', marginBottom: '1rem', fontWeight: '600' }}>
          📊 {title}
        </h4>
      )}
      
      {/* Backtest Period */}
      <div style={periodStyle}>
        <div style={periodLabelStyle}>
          📅 Backtest Period
        </div>
        <div style={periodDateStyle}>
          {backtest.startDate} - {backtest.endDate}
        </div>
        {!compact && backtest.revisionVersion && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#8b949e' }}>
            Revision v{backtest.revisionVersion}
          </div>
        )}
      </div>

      {/* Performance Metrics */}
      <div style={metricsGridStyle}>
        <div style={metricCardStyle}>
          <div style={metricLabelStyle}>
            📈 CAGR
          </div>
          <div style={{
            ...metricValueStyle,
            color: isPositiveReturn ? '#3fb950' : '#f85149'
          }}>
            {formatPercent(backtest.cagrAnnualReturn)}
          </div>
          <div style={metricDescStyle}>Annual Return</div>
        </div>
        
        <div style={metricCardStyle}>
          <div style={metricLabelStyle}>
            💹 Total Return
          </div>
          <div style={{
            ...metricValueStyle,
            color: isPositiveTotalReturn ? '#3fb950' : '#f85149'
          }}>
            {formatPercent(backtest.totalReturn)}
          </div>
          <div style={metricDescStyle}>Over Period</div>
        </div>
        
        {!compact && (
          <div style={metricCardStyle}>
            <div style={metricLabelStyle}>
              📉 Max Drawdown
            </div>
            <div style={{
              ...metricValueStyle,
              color: '#f85149'
            }}>
              {formatPercent(backtest.maxDrawdown)}
            </div>
            <div style={metricDescStyle}>Worst Loss</div>
          </div>
        )}
      </div>

      {/* Compact view - show drawdown in footer */}
      {compact && backtest.maxDrawdown && (
        <div style={{ textAlign: 'center', paddingTop: '1rem', borderTop: '1px solid #30363d' }}>
          <small style={{ color: '#8b949e' }}>
            Max Drawdown: <span style={{ color: '#f85149', fontWeight: '600' }}>{formatPercent(backtest.maxDrawdown)}</span>
          </small>
        </div>
      )}

      {/* Additional metrics for non-compact view */}
      {!compact && (backtest.sharpe || backtest.completedAt) && (
        <div style={{
          borderTop: '1px solid #30363d',
          paddingTop: '1rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '1rem'
        }}>
          {backtest.sharpe && (
            <div style={{...metricCardStyle, background: '#1c2128'}}>
              <div style={metricLabelStyle}>Sharpe Ratio</div>
              <div style={{...metricValueStyle, color: '#e6edf3', fontSize: '1.2rem'}}>
                {backtest.sharpe.toFixed(2)}
              </div>
            </div>
          )}
          {backtest.completedAt && (
            <div style={{...metricCardStyle, background: '#1c2128'}}>
              <div style={metricLabelStyle}>Completed</div>
              <div style={{...metricValueStyle, color: '#e6edf3', fontSize: '1rem'}}>
                {new Date(backtest.completedAt).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info footer */}
      <div style={infoStyle}>
        <small style={{ color: '#e3b341', fontSize: '0.8rem', fontWeight: '500' }}>
          ℹ️ Results based on historical data. Past performance does not guarantee future results.
        </small>
      </div>
    </div>
  );
};

export default BacktestResults; 